import React, { useState, useEffect } from 'react';
import QuestionnaireQuestionList from './QuestionnaireQuestionList';
import LLMQuestionnairePopup from './LLMQuestionnairePopup';
import CriteriaView from './Shards/CriteriaView';
// import ImprovementsView from './Shards/ImprovementsView';
import CheatingProbabilityIndicator from './Shards/CheatingProbabilityIndicator';
import CheatingDetailsModal from '../CheatingComponents/CheatingDetailsModal';
import { useSession } from '../Utils/SessionContext';
import QuestionnaireStatus from './Utils/QuestionnaireStatus';
import { Link } from 'react-router-dom';

/**
 * EvalQuestionnaire Component
 * Reusable component for displaying questionnaire responses for both talents and candidates
 */
const EvalQuestionnaire = ({ 
  questionnaireData,
  contextType = 'talent' // 'talent' or 'candidate'
}) => {
  const { user } = useSession();
  // State to store parsed data
  const [parsedData, setParsedData] = useState({
    questions: [],
    duration: null,
    status: null,
    cheatingEvents: [],
    cheatingAssessment: null
  });
  
  // State for popups
  const [showLlmPopup, setShowLlmPopup] = useState(false);
  const [showCriteriaPopup, setShowCriteriaPopup] = useState(false);
  // const [showImprovementsPopup, setShowImprovementsPopup] = useState(false);
  const [showCheatingModal, setShowCheatingModal] = useState(false);
  // const [submissionResult, setSubmissionResult] = useState(null); // null, true (success), or false (error)
  
  // Function to parse and prepare questionnaire data
  const prepareQuestionnaireData = () => {
    if (!questionnaireData) {
      return;
    }
    
    // Extract questions from questionnaire_final_json or questionnaire_copy_json if no answers yet
    const questions = questionnaireData.questionnaire_final_json?.questions?.length > 0 
      ? questionnaireData.questionnaire_final_json.questions 
      : (questionnaireData.questionnaire_copy_json?.questions || []);
    
    // Calculate actual completion duration only if both start and end dates exist
    let completionDuration = null;
    if (questionnaireData.questionnaire_start_date && questionnaireData.questionnaire_end_date) {
      const startDate = new Date(questionnaireData.questionnaire_start_date);
      const endDate = new Date(questionnaireData.questionnaire_end_date);
      const durationMs = endDate - startDate;
      completionDuration = Math.round(durationMs / (1000 * 60)); // Convert to minutes
    }
    
    // Get max duration separately
    let maxDuration = null;
    if (questionnaireData.max_duration_minutes) {
      maxDuration = parseInt(questionnaireData.max_duration_minutes);
    }
    
    // Extract status
    const status = questionnaireData.questionnaire_status || null;
    
    // Extract cheating events
    const cheatingEvents = questionnaireData.questionnaire_cheating_events_json || [];
    
    // Extract cheating assessment
    const cheatingAssessment = questionnaireData.questionnaire_cheating_assessment_json || null;
    
    setParsedData({
      questions,
      completionDuration,
      maxDuration,
      status,
      cheatingEvents,
      cheatingAssessment
    });
  };
  
  // Parse data when questionnaireData changes
  useEffect(() => {
    prepareQuestionnaireData();
  }, [questionnaireData]);
  
  // Dummy function for onRegenerateQuestion since we don't need regeneration functionality
  const handleRegenerateQuestion = () => {
    // This function is required by QuestionList but won't do anything in this context
  };

  
  // Format duration for display
  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return 'Not available';
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${minutes}m`;
    } else if (remainingMinutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${remainingMinutes}m`;
    }
  };

  return (
    <div className="space-y-4">
      {/* Tab-related header - only show when questionnaire exists */}
      {parsedData.questions && parsedData.questions.length > 0 && (
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6 border border-gray-200">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">Questionnaire Response</h2>
              
              {/* Integrity Report - standalone button */}
              {parsedData.cheatingAssessment && (
                <button
                  onClick={() => setShowCheatingModal(true)}
                  className="h-8 px-3 text-sm bg-gradient-to-r from-green-500 to-red-500 text-white rounded hover:from-green-600 hover:to-red-600 transition-colors flex items-center whitespace-nowrap"
                >
                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                  Integrity Report
                </button>
              )}
              
              {/* Actions Dropdown - 3 dots icon */}
              <div className="relative group">
                <button className="h-8 px-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:border-gray-400 transition-colors flex items-center">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <div className="py-1">
                    {/* View Criteria */}
                    <button
                      onClick={() => setShowCriteriaPopup(true)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 616 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                      View Criteria
                    </button>
                    
                    {/* Solve with LLM - only visible for Superadmin */}
                    {user?.role === 'Superadmin' && (
                      <>
                        <div className="border-t border-gray-200 my-1"></div>
                        <button
                          onClick={() => setShowLlmPopup(true)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                          </svg>
                          Solve with LLM
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <p className="text-gray-600 text-l">
              Response to the questionnaire which helps assess the {contextType}'s knowledge and experience.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {/* Status first */}
              {parsedData.status && (
                <QuestionnaireStatus status={parsedData.status} />
              )}
              
              {/* Vertical divider */}
              {parsedData.status && (
                <div className="h-6 w-px bg-gray-300"></div>
              )}
              
              {/* Show max duration if available */}
              {parsedData.maxDuration !== null && (
                <div className="inline-flex items-center bg-gray-700 text-white px-3 py-1 rounded-full text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">Max:&nbsp;</span> {parsedData.maxDuration}m
                </div>
              )}
              
              {/* Show completion time if available */}
              {parsedData.completionDuration !== null && (
                <div className="inline-flex items-center bg-black text-white px-3 py-1 rounded-full text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">Completion Time:&nbsp;</span> {formatDuration(parsedData.completionDuration)}
                </div>
              )}
              
              {/* Cheating Probability */}
              {parsedData.cheatingAssessment && (
                <CheatingProbabilityIndicator 
                  cheatingAssessment={parsedData.cheatingAssessment}
                  cheatingEvents={parsedData.cheatingEvents}
                  onClick={() => setShowCheatingModal(true)}
                />
              )}
            </div>
          </div>
        </div>
        </div>
      )}

      {parsedData.questions && parsedData.questions.length > 0 ? (
        <QuestionnaireQuestionList
          questions={parsedData.questions} 
          onRegenerateQuestion={handleRegenerateQuestion}
          showAnswers={true}
        />
      ) : (
        <div className="space-y-4">
          {/* Empty State */}
          <div className="p-8 bg-gray-50 rounded-lg text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Questionnaire Available</h3>
            <p className="text-gray-600 mb-4">
              This evaluation does not have a questionnaire.
            </p>
            <p className="text-gray-500 text-sm mb-4">
              Go to the the Evaluation Tempalte via the Quick Links and generate one, then enable it in settings if disabled.
            </p>
          </div>
        </div>
      )}
      
      {/* LLM Questionnaire Popup */}
      <LLMQuestionnairePopup
        show={showLlmPopup}
        title="LLM Questionnaire Completion"
        eval_id={questionnaireData?.eval_id}
        onSubmit={(success) => {
          console.log("LLM Questionnaire onSubmit called with success:", success);
          setShowLlmPopup(false);
          
          // Show a JavaScript alert instead of an inline message
          if (success) {
            alert("Questionnaire completed successfully! Please refresh the page to see the results.");
          } else {
            alert("Failed to complete questionnaire. Please try again.");
          }
        }}
        onCancel={() => setShowLlmPopup(false)}
      />
      
      {/* Criteria View Popup */}
      <CriteriaView
        show={showCriteriaPopup}
        onClose={() => setShowCriteriaPopup(false)}
        criteria_json={questionnaireData?.questionnaires?.questionnaire_criteria_json}
      />
      
      {/* Improvements View Popup */}
      {/* <ImprovementsView
        show={showImprovementsPopup}
        onClose={() => setShowImprovementsPopup(false)}
        questions={parsedData.questions}
      /> */}
      
      {/* Cheating Details Modal */}
      <CheatingDetailsModal
        show={showCheatingModal}
        onClose={() => setShowCheatingModal(false)}
        cheatingData={{
          cheating_assessment_json: parsedData.cheatingAssessment,
          cheating_events_json: parsedData.cheatingEvents,
          final_cheating_analysis: {
            final_cheating_probability: parsedData.cheatingAssessment?.cheatingProbability 
              ? parseFloat(parsedData.cheatingAssessment.cheatingProbability.replace('%', '')) / 100
              : 0,
            rationale: parsedData.cheatingAssessment?.rationale || []
          }
        }}
        completionTime={parsedData.completionDuration}
      />
    </div>
  );
};

export default EvalQuestionnaire;
